/**
 * GenericFrameworkPage - Reusable component for non-CyberForte compliance frameworks
 * 
 * NOTE: ISO 27001 and SOC 2 are now managed through CyberForte Unified Compliance.
 * This component is for: Essential8, PCIDSS, HIPAA, ISO42001
 */

import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Shield, CheckCircle, XCircle, MinusCircle, Edit, Download, AlertTriangle, User, Calendar, FileText, ShieldCheck, Trash2, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import GapAssessmentImport from '../components/GapAssessmentImport';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Framework configurations
// NOTE: ISO27001 and SOC2 removed - now managed via CyberForte Unified Compliance
const FRAMEWORK_CONFIG = {
  Essential8: {
    displayName: 'Essential 8',
    description: 'Australian Cyber Security Centre - Essential Eight Maturity Model',
    bannerText: 'The Essential Eight are baseline mitigation strategies to protect against various cyber threats. Developed by the Australian Cyber Security Centre (ACSC).',
    icon: Shield,
    autoImport: true,
    importEndpoint: '/controls/import/essential8',
    recommendations: {
      'E8-1': 'Implement application control to prevent execution of unapproved programs.',
      'E8-7': 'Deploy multi-factor authentication for all users.',
    }
  },
  PCIDSS: {
    displayName: 'PCI DSS',
    description: 'Payment Card Industry Data Security Standard',
    bannerText: 'PCI DSS requirements apply to all entities involved in payment card processing.',
    icon: Shield,
    autoImport: true,
    importEndpoint: '/controls/import/pcidss',
    recommendations: {}
  },
  HIPAA: {
    displayName: 'HIPAA',
    description: 'Health Insurance Portability and Accountability Act',
    bannerText: 'HIPAA establishes national standards to protect sensitive patient health information.',
    icon: Shield,
    autoImport: true,
    importEndpoint: '/controls/import/hipaa',
    recommendations: {}
  },
  ISO42001: {
    displayName: 'ISO 42001',
    description: 'Artificial Intelligence Management System',
    bannerText: 'ISO 42001 provides requirements for establishing, implementing, and improving an AI Management System.',
    icon: Shield,
    autoImport: true,
    importEndpoint: '/controls/import/iso42001',
    recommendations: {}
  },
  SOCI: {
    displayName: 'SOCI',
    description: 'Security of Critical Infrastructure Act 2018 (Australia)',
    bannerText: 'SOCI establishes obligations for owners and operators of critical infrastructure to protect against cyber, personnel, supply chain, and physical/natural-hazard threats.',
    icon: Shield,
    autoImport: true,
    importEndpoint: '/controls/import/soci',
    recommendations: {}
  }
};

export default function GenericFrameworkPage({ frameworkKey }) {
  const config = FRAMEWORK_CONFIG[frameworkKey];
  const { user, viewingOrg } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingControl, setEditingControl] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterOwner, setFilterOwner] = useState('all');
  const [sortBy, setSortBy] = useState('control_id');
  const [viewMode, setViewMode] = useState('all');
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [evidenceDescription, setEvidenceDescription] = useState('');
  const [evidenceTags, setEvidenceTags] = useState('');
  const [generatingReport, setGeneratingReport] = useState(false);

  const handleCirmpReport = async () => {
    setGeneratingReport(true);
    try {
      const params = viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';
      const res = await axios.get(`${API}/frameworks/SOCI/cirmp-report${params}`);
      const d = res.data;

      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 50;

      // Cover band
      doc.setFillColor(146, 64, 14); // amber-800
      doc.rect(0, 0, pageW, 110, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(22);
      doc.text('CIRMP Annual Report', margin, 50);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.text('Critical Infrastructure Risk Management Programme', margin, 72);
      doc.setFontSize(9);
      doc.text('SOCI Act 2018 (Cth) — Section 30AG/30AH submission', margin, 90);

      doc.setTextColor(15, 23, 42);
      let y = 140;
      const periodStart = new Date(d.report_period.start).toLocaleDateString();
      const periodEnd = new Date(d.report_period.end).toLocaleDateString();

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text(d.organization, margin, y);
      y += 18;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      if (d.abn) { doc.text(`ABN: ${d.abn}`, margin, y); y += 14; }
      doc.text(`Reporting period: ${periodStart} — ${periodEnd}`, margin, y); y += 14;
      doc.text(`Generated: ${new Date(d.generated_at).toLocaleString()}`, margin, y); y += 24;

      // Section 1 — Executive Summary
      const heading = (txt) => {
        doc.setFillColor(241, 245, 249);
        doc.rect(margin, y - 12, pageW - margin * 2, 22, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(15, 23, 42);
        doc.text(txt, margin + 6, y + 4);
        y += 22;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
      };

      heading('1. Executive Summary');
      const cs = d.controls_summary;
      const summaryLines = [
        `• ${cs.total} CIRMP controls under management; ${cs.compliance_pct}% assessed as compliant.`,
        `• Compliant: ${cs.compliant} | Partially Compliant: ${cs.partial} | Non-compliant: ${cs.non_compliant} | Not yet assessed: ${cs.not_assessed}`,
        `• ${d.risks.length} risks tracked in the risk register.`,
        `• ${d.cyber_incidents.length} reportable cyber incident(s) during the reporting period (out of ${d.incidents_total} total incidents).`,
        `• Policy acknowledgement completion: ${d.policy_acknowledgements.completion_pct}% (${d.policy_acknowledgements.acknowledged}/${d.policy_acknowledgements.total}).`,
        `• Suppliers: ${d.suppliers_summary.total} tracked, ${d.suppliers_summary.high_risk} high-risk.`,
      ];
      summaryLines.forEach(line => {
        const lns = doc.splitTextToSize(line, pageW - margin * 2);
        doc.text(lns, margin, y);
        y += lns.length * 13 + 2;
      });
      y += 8;

      // Section 2 — Hazard Domains (controls by category)
      heading('2. Hazard Domains — CIRMP Control Status');
      const categoryOrder = ['Cyber & Information Security', 'Personnel', 'Supply Chain', 'Physical & Natural', 'Governance'];
      const ordered = [];
      categoryOrder.forEach(c => { if (d.controls_by_category[c]) ordered.push([c, d.controls_by_category[c]]); });
      Object.entries(d.controls_by_category).forEach(([c, list]) => {
        if (!categoryOrder.includes(c)) ordered.push([c, list]);
      });

      const statusLabel = (s) => {
        const v = (s || '').toLowerCase();
        if (v === 'compliant') return 'Compliant';
        if (v === 'partial') return 'Partially Compliant';
        if (v === 'non_compliant' || v === 'non-compliant') return 'Non-Compliant';
        return 'Not Assessed';
      };

      ordered.forEach(([cat, list]) => {
        autoTable(doc, {
          startY: y,
          head: [[cat, 'Status', 'Notes']],
          body: list.map(c => [
            `${c.control_id}\n${c.title}`,
            statusLabel(c.status),
            (c.notes || '').slice(0, 200),
          ]),
          styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
          headStyles: { fillColor: [146, 64, 14], textColor: [255, 255, 255], fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          columnStyles: { 0: { cellWidth: 220 }, 1: { cellWidth: 90 }, 2: { cellWidth: 'auto' } },
          didParseCell: (h) => {
            if (h.section === 'body' && h.column.index === 1) {
              const v = (h.cell.raw || '').toString();
              if (v === 'Compliant') { h.cell.styles.textColor = [5, 150, 105]; h.cell.styles.fontStyle = 'bold'; }
              else if (v === 'Non-Compliant') { h.cell.styles.textColor = [220, 38, 38]; h.cell.styles.fontStyle = 'bold'; }
              else if (v === 'Partially Compliant') { h.cell.styles.textColor = [217, 119, 6]; }
            }
          },
          margin: { left: margin, right: margin },
        });
        y = doc.lastAutoTable.finalY + 12;
      });

      // Section 3 — Material Risks
      doc.addPage();
      y = 60;
      heading('3. Material Risks Identified');
      if (d.risks.length === 0) {
        doc.text('No risks recorded in the risk register.', margin, y); y += 16;
      } else {
        autoTable(doc, {
          startY: y,
          head: [['Risk', 'Category', 'Level', 'Status', 'Owner', 'Treatment']],
          body: d.risks.map(r => [
            r.title || '-',
            r.category || '-',
            r.level || '-',
            r.status || '-',
            r.owner || '-',
            (r.treatment || '').slice(0, 150),
          ]),
          styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
          headStyles: { fillColor: [146, 64, 14], textColor: [255, 255, 255], fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          margin: { left: margin, right: margin },
        });
        y = doc.lastAutoTable.finalY + 12;
      }

      // Section 4 — Cyber Incidents
      heading('4. Reportable Cyber Incidents');
      if (d.cyber_incidents.length === 0) {
        doc.text('No reportable cyber incidents during the reporting period.', margin, y); y += 16;
      } else {
        autoTable(doc, {
          startY: y,
          head: [['Title', 'Severity', 'Occurred', 'Status', 'Summary']],
          body: d.cyber_incidents.map(i => [
            i.title || '-',
            i.severity || '-',
            i.occurred_at ? new Date(i.occurred_at).toLocaleDateString() : '-',
            i.status || '-',
            i.summary || '',
          ]),
          styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
          headStyles: { fillColor: [146, 64, 14], textColor: [255, 255, 255], fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          margin: { left: margin, right: margin },
        });
        y = doc.lastAutoTable.finalY + 12;
      }

      // Section 5 — Board approval / declaration
      if (y > pageH - 200) { doc.addPage(); y = 60; }
      heading('5. Board Approval & Currency Declaration');
      const declaration = [
        'The above Critical Infrastructure Risk Management Programme has been adopted by the Board (or equivalent governing body)',
        'and is currency-reviewed in accordance with sub-section 30AG(2) of the Security of Critical Infrastructure Act 2018 (Cth).',
        '',
        'Signed: _____________________________________     Date: _________________',
        'Name & title: ____________________________________________________________',
      ];
      declaration.forEach(line => {
        const ln = doc.splitTextToSize(line, pageW - margin * 2);
        doc.text(ln, margin, y);
        y += ln.length * 13 + 4;
      });

      // Footer on every page
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(148, 163, 184);
        doc.text(`${d.organization} · CIRMP Annual Report`, margin, pageH - 24);
        doc.text(`Page ${i} of ${totalPages}`, pageW - margin, pageH - 24, { align: 'right' });
        doc.setTextColor(15, 23, 42);
      }

      const fname = `CIRMP_Annual_Report_${d.organization.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
      doc.save(fname);
      toast.success('CIRMP report generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const getViewTenantParam = () => {
    return viewingOrg?.id ? `&view_tenant_id=${viewingOrg.id}` : '';
  };

  useEffect(() => {
    fetchControls();
  }, [viewingOrg, frameworkKey]);

  const fetchControls = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/controls?framework=${frameworkKey}${getViewTenantParam()}`);
      
      // Auto-import if configured and no controls exist
      if (response.data.length === 0 && config.autoImport && config.importEndpoint) {
        try {
          await axios.post(`${API}${config.importEndpoint}`);
          const importedResponse = await axios.get(`${API}/controls?framework=${frameworkKey}${getViewTenantParam()}`);
          setControls(importedResponse.data);
          toast.success(`${config.displayName} controls imported successfully`);
        } catch (importError) {
          console.error('Auto-import failed:', importError);
          setControls([]);
        }
      } else {
        setControls(response.data);
      }
    } catch (error) {
      toast.error(`Failed to fetch ${config.displayName} controls`);
      setControls([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvidenceFiles = async (controlId) => {
    try {
      const response = await axios.get(`${API}/evidence/${controlId}`);
      setEvidenceFiles(response.data);
    } catch (error) {
      setEvidenceFiles([]);
    }
  };

  const handleFileUpload = async (e, controlId) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('control_id', controlId);
    formData.append('description', evidenceDescription);
    formData.append('tags', evidenceTags);

    try {
      await axios.post(`${API}/evidence/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Evidence uploaded successfully');
      fetchEvidenceFiles(controlId);
      setEvidenceDescription('');
      setEvidenceTags('');
      e.target.value = null;
    } catch (error) {
      toast.error('Failed to upload evidence');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteEvidence = async (evidenceId, controlId) => {
    if (!confirm('Are you sure you want to delete this evidence?')) return;

    try {
      await axios.delete(`${API}/evidence/${evidenceId}`);
      toast.success('Evidence deleted successfully');
      fetchEvidenceFiles(controlId);
    } catch (error) {
      toast.error('Failed to delete evidence');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingControl) return;
    
    if (!isAdmin) {
      toast.error('Only admins can modify control status');
      return;
    }

    try {
      const params = viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';
      await axios.patch(`${API}/controls/${editingControl.id}${params}`, {
        status: editingControl.status,
        category: editingControl.category,
        evidence: editingControl.evidence,
        comments: editingControl.comments,
        owner: editingControl.owner,
        due_date: editingControl.due_date,
        remediation_status: editingControl.remediation_status,
        action_plan: editingControl.action_plan
      });
      toast.success('Control updated successfully');
      setShowDialog(false);
      setEditingControl(null);
      fetchControls();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Only admins can modify control status');
      } else {
        toast.error('Failed to update control');
      }
    }
  };

  const handleStatusChange = async (controlId, newStatus) => {
    if (!isAdmin) {
      toast.error('Only admins can modify control status');
      return;
    }
    
    try {
      const params = viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';
      await axios.patch(`${API}/controls/${controlId}${params}`, { status: newStatus });
      toast.success('Status updated successfully');
      fetchControls();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleExport = () => {
    try {
      const csvHeaders = ['Control ID', 'Title', 'Category', 'Status', 'Owner', 'Due Date', 'Remediation Status', 'Requirement', 'Evidence', 'Comments'];
      const csvData = filteredControls.map(control => [
        control.control_id,
        control.title,
        control.category || '',
        control.status || 'Pending',
        control.owner || 'Unassigned',
        control.due_date || 'Not set',
        control.remediation_status || 'Pending',
        control.requirement || '',
        control.evidence || '',
        control.comments || ''
      ]);

      const csvContent = [
        csvHeaders.join(','),
        ...csvData.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${frameworkKey}_Controls_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Controls exported successfully');
    } catch (error) {
      toast.error('Failed to export controls');
    }
  };

  const generateRecommendation = (control) => {
    return config.recommendations?.[control.control_id] || 
           control.action_plan || 
           'Develop and implement appropriate safeguards and document evidence.';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Yes': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'No': return <XCircle className="w-5 h-5 text-red-600" />;
      case 'Partially': return <MinusCircle className="w-5 h-5 text-slate-600" />;
      default: return <MinusCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const stats = {
    total: controls.length,
    compliant: controls.filter(c => c.status === 'Yes').length,
    nonCompliant: controls.filter(c => c.status === 'No').length,
    partial: controls.filter(c => c.status === 'Partially').length,
    notApplicable: controls.filter(c => c.status === 'N.A').length,
    notAssessed: controls.filter(c => !c.status).length
  };

  const chartData = [
    { name: 'Compliant', value: stats.compliant, color: '#22c55e' },
    { name: 'Non-Compliant', value: stats.nonCompliant, color: '#ef4444' },
    { name: 'Partially', value: stats.partial, color: '#eab308' },
    { name: 'N/A', value: stats.notApplicable, color: '#6b7280' },
    { name: 'Not Assessed', value: stats.notAssessed, color: '#94a3b8' }
  ].filter(item => item.value > 0);

  const categories = ['all', ...new Set(controls.map(c => c.category).filter(Boolean))];
  const owners = ['all', ...new Set(controls.map(c => c.owner).filter(Boolean))];

  const filteredControls = controls
    .filter(control => {
      if (viewMode === 'non-compliant') {
        const isNonCompliant = control.status === 'No' || control.status === 'Partially' || !control.status;
        if (!isNonCompliant) return false;
      }
      
      const matchesSearch = control.control_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        control.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        control.requirement?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (control.owner && control.owner.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesStatus = filterStatus === 'all' || control.status === filterStatus;
      const matchesCategory = filterCategory === 'all' || control.category === filterCategory;
      const matchesOwner = filterOwner === 'all' || control.owner === filterOwner;
      return matchesSearch && matchesStatus && matchesCategory && matchesOwner;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'control_id': return (a.control_id || '').localeCompare(b.control_id || '');
        case 'title': return (a.title || '').localeCompare(b.title || '');
        case 'category': return (a.category || '').localeCompare(b.category || '');
        case 'status':
          const statusOrder = { 'Yes': 1, 'Partially': 2, 'No': 3, 'N.A': 4 };
          return (statusOrder[a.status] || 5) - (statusOrder[b.status] || 5);
        case 'owner': return (a.owner || 'zzz').localeCompare(b.owner || 'zzz');
        default: return 0;
      }
    });

  const groupedControls = filteredControls.reduce((groups, control) => {
    const category = control.category || 'Uncategorized';
    if (!groups[category]) groups[category] = [];
    groups[category].push(control);
    return groups;
  }, {});

  const sortedCategories = Object.keys(groupedControls).sort();
  const IconComponent = config.icon;

  return (
    <div className="p-6 space-y-6" data-testid={`${frameworkKey.toLowerCase()}-page`}>
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-3">
            <IconComponent className="w-8 h-8 text-slate-600" />
            <h1 className="text-3xl font-heading font-bold">{config.displayName}</h1>
          </div>
          <p className="text-gray-700 mt-2">{config.description}</p>
        </div>
        <div className="flex gap-2">
          <div className="flex border border-border rounded-sm overflow-hidden">
            <button
              onClick={() => setViewMode('all')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                viewMode === 'all' ? 'bg-slate-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              data-testid="view-all-btn"
            >
              <ShieldCheck className="w-4 h-4 inline mr-2" />All Controls
            </button>
            <button
              onClick={() => setViewMode('non-compliant')}
              className={`px-4 py-2 text-sm font-medium transition-colors border-l ${
                viewMode === 'non-compliant' ? 'bg-slate-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              data-testid="view-non-compliant-btn"
            >
              <AlertTriangle className="w-4 h-4 inline mr-2" />Non-Compliant
            </button>
          </div>
          <div className="flex gap-2">
            <GapAssessmentImport 
              framework={frameworkKey} 
              frameworkDisplayName={config.displayName} 
              onImportComplete={fetchControls}
              viewTenantId={viewingOrg?.id}
            />
            <Button variant="outline" onClick={handleExport} data-testid="export-btn">
              <Download className="w-4 h-4 mr-2" />Export
            </Button>
            {frameworkKey === 'SOCI' && (
              <Button
                onClick={handleCirmpReport}
                disabled={generatingReport}
                className="bg-amber-700 text-white hover:bg-amber-800"
                data-testid="cirmp-report-btn"
              >
                <FileDown className="w-4 h-4 mr-2" />
                {generatingReport ? 'Generating...' : 'CIRMP Annual Report'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Compliance Banner */}
      <div className="bg-slate-50 border border-slate-700 rounded-sm p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-slate-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-slate-900">{config.displayName}</h3>
            <p className="text-sm text-slate-700 mt-1">{config.bannerText}</p>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div className="bg-white border border-border rounded-sm p-4">
          <p className="text-sm text-gray-700">Total Controls</p>
          <p className="text-2xl font-bold mt-1" data-testid="total-controls">{stats.total}</p>
        </div>
        <div className="bg-white border border-green-200 rounded-sm p-4">
          <p className="text-sm text-gray-700">Compliant</p>
          <p className="text-2xl font-bold mt-1 text-green-600" data-testid="compliant-count">{stats.compliant}</p>
        </div>
        <div className="bg-white border border-red-200 rounded-sm p-4">
          <p className="text-sm text-gray-700">Non-Compliant</p>
          <p className="text-2xl font-bold mt-1 text-red-600" data-testid="non-compliant-count">{stats.nonCompliant}</p>
        </div>
        <div className="bg-white border border-yellow-200 rounded-sm p-4">
          <p className="text-sm text-gray-700">Partially Compliant</p>
          <p className="text-2xl font-bold mt-1 text-yellow-600">{stats.partial}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-sm p-4">
          <p className="text-sm text-gray-700">N/A</p>
          <p className="text-2xl font-bold mt-1 text-gray-600">{stats.notApplicable}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <p className="text-sm text-gray-700">Not Assessed</p>
          <p className="text-2xl font-bold mt-1 text-slate-500">{stats.notAssessed}</p>
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="bg-white border border-border rounded-sm p-6">
          <h3 className="font-semibold mb-4">Compliance Status Distribution</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={chartData} cx="50%" cy="50%" labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80} dataKey="value">
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-border rounded-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-lg">Filter & Sort Controls</h3>
          <Button variant="outline" size="sm" onClick={() => {
            setSearchTerm(''); setFilterStatus('all'); setFilterCategory('all'); setFilterOwner('all'); setSortBy('control_id');
          }}>Clear All</Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <Label className="text-sm font-medium">Search</Label>
            <Input placeholder="Control ID, title, owner..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} className="mt-1" data-testid="search-input" />
          </div>
          
          <div>
            <Label className="text-sm font-medium">Status</Label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full border border-input rounded-sm px-3 py-2 text-sm mt-1 bg-white" data-testid="status-filter">
              <option value="all">All Statuses</option>
              <option value="Yes">✓ Compliant</option>
              <option value="No">✗ Non-Compliant</option>
              <option value="Partially">◐ Partially</option>
              <option value="N.A">− Not Applicable</option>
            </select>
          </div>
          
          <div>
            <Label className="text-sm font-medium">Category</Label>
            <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full border border-input rounded-sm px-3 py-2 text-sm mt-1 bg-white">
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat === 'all' ? 'All Categories' : cat}</option>
              ))}
            </select>
          </div>
          
          <div>
            <Label className="text-sm font-medium">Owner</Label>
            <select value={filterOwner} onChange={(e) => setFilterOwner(e.target.value)}
              className="w-full border border-input rounded-sm px-3 py-2 text-sm mt-1 bg-white">
              <option value="all">All Owners</option>
              {owners.filter(o => o !== 'all').map(owner => (
                <option key={owner} value={owner}>{owner}</option>
              ))}
            </select>
          </div>
          
          <div>
            <Label className="text-sm font-medium">Sort By</Label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
              className="w-full border border-input rounded-sm px-3 py-2 text-sm mt-1 bg-white">
              <option value="control_id">Control ID</option>
              <option value="title">Title (A-Z)</option>
              <option value="category">Category</option>
              <option value="status">Compliance Status</option>
              <option value="owner">Owner</option>
            </select>
          </div>
        </div>
        
        <div className="flex items-center justify-between mt-4 pt-4 border-t">
          <p className="text-sm text-gray-700">
            Showing <span className="font-semibold">{filteredControls.length}</span> of <span className="font-semibold">{controls.length}</span> controls
          </p>
        </div>
      </div>

      {/* Controls Display */}
      <div className="space-y-6">
        {loading ? (
          <div className="text-center p-8 text-gray-700">Loading controls...</div>
        ) : filteredControls.length === 0 ? (
          <div className="bg-white border border-border rounded-sm p-8 text-center text-gray-700">
            {controls.length === 0 ? 'No controls found' : 'No controls match your current filters'}
          </div>
        ) : (
          sortedCategories.map((category) => (
            <div key={category} className="bg-white border border-border rounded-sm overflow-hidden">
              <div className="bg-slate-100 px-4 py-3 border-b">
                <h3 className="font-semibold text-lg text-slate-800">{category}</h3>
                <p className="text-sm text-slate-600">{groupedControls[category].length} control{groupedControls[category].length !== 1 ? 's' : ''}</p>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-3 text-sm font-semibold w-28">Control ID</th>
                      <th className="text-left p-3 text-sm font-semibold">Title & Requirement</th>
                      <th className="text-left p-3 text-sm font-semibold w-32">Status</th>
                      <th className="text-left p-3 text-sm font-semibold w-28">Owner</th>
                      <th className="text-left p-3 text-sm font-semibold w-24">Due Date</th>
                      <th className="text-right p-3 text-sm font-semibold w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedControls[category].map((control) => (
                      <tr key={control.id} className="border-b hover:bg-slate-50" data-testid={`control-row-${control.control_id}`}>
                        <td className="p-3">
                          <span className="font-mono text-sm font-medium text-slate-700">{control.control_id}</span>
                        </td>
                        <td className="p-3">
                          <p className="font-medium text-slate-900">{control.title}</p>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{control.requirement}</p>
                        </td>
                        <td className="p-3">
                          {isAdmin ? (
                            <select
                              value={control.status || ''}
                              onChange={(e) => handleStatusChange(control.control_id, e.target.value)}
                              className="border border-input rounded px-2 py-1 text-sm bg-white"
                              data-testid={`status-select-${control.control_id}`}
                            >
                              <option value="">Not Assessed</option>
                              <option value="Yes">✓ Compliant</option>
                              <option value="No">✗ Non-Compliant</option>
                              <option value="Partially">◐ Partial</option>
                              <option value="N.A">− N/A</option>
                            </select>
                          ) : (
                            <div className="flex items-center gap-2">
                              {getStatusIcon(control.status)}
                              <span className="text-sm">{control.status || 'Not Assessed'}</span>
                            </div>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1 text-sm text-gray-700">
                            <User className="w-4 h-4" />
                            <span>{control.owner || 'Unassigned'}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1 text-sm text-gray-700">
                            <Calendar className="w-4 h-4" />
                            <span>{control.due_date || 'Not set'}</span>
                          </div>
                        </td>
                        <td className="p-3 text-right">
                          <Dialog open={showDialog && editingControl?.id === control.id}
                            onOpenChange={(open) => {
                              setShowDialog(open);
                              if (open) {
                                setEditingControl({...control});
                                fetchEvidenceFiles(control.control_id);
                              }
                            }}>
                            <DialogTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`edit-btn-${control.control_id}`}>
                                <Edit className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle>Edit Control: {control.control_id}</DialogTitle>
                              </DialogHeader>
                              {editingControl && (
                                <form onSubmit={handleUpdate} className="space-y-4">
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <Label>Status</Label>
                                      <select value={editingControl.status || ''} disabled={!isAdmin}
                                        onChange={(e) => setEditingControl({...editingControl, status: e.target.value})}
                                        className="w-full border border-input rounded-sm px-3 py-2 text-sm mt-1 bg-white">
                                        <option value="">Not Assessed</option>
                                        <option value="Yes">Compliant</option>
                                        <option value="No">Non-Compliant</option>
                                        <option value="Partially">Partially</option>
                                        <option value="N.A">Not Applicable</option>
                                      </select>
                                    </div>
                                    <div>
                                      <Label>Owner</Label>
                                      <Input value={editingControl.owner || ''} disabled={!isAdmin}
                                        onChange={(e) => setEditingControl({...editingControl, owner: e.target.value})}
                                        className="mt-1" />
                                    </div>
                                    <div>
                                      <Label>Due Date</Label>
                                      <Input type="date" value={editingControl.due_date || ''} disabled={!isAdmin}
                                        onChange={(e) => setEditingControl({...editingControl, due_date: e.target.value})}
                                        className="mt-1" />
                                    </div>
                                    <div>
                                      <Label>Remediation Status</Label>
                                      <select value={editingControl.remediation_status || ''} disabled={!isAdmin}
                                        onChange={(e) => setEditingControl({...editingControl, remediation_status: e.target.value})}
                                        className="w-full border border-input rounded-sm px-3 py-2 text-sm mt-1 bg-white">
                                        <option value="">Select...</option>
                                        <option value="Pending">Pending</option>
                                        <option value="In Progress">In Progress</option>
                                        <option value="Under Review">Under Review</option>
                                        <option value="Completed">Completed</option>
                                      </select>
                                    </div>
                                  </div>
                                  
                                  <div>
                                    <Label>Evidence Notes</Label>
                                    <Textarea value={editingControl.evidence || ''} disabled={!isAdmin}
                                      onChange={(e) => setEditingControl({...editingControl, evidence: e.target.value})}
                                      className="mt-1" rows={3} />
                                  </div>
                                  
                                  <div>
                                    <Label>Comments</Label>
                                    <Textarea value={editingControl.comments || ''} disabled={!isAdmin}
                                      onChange={(e) => setEditingControl({...editingControl, comments: e.target.value})}
                                      className="mt-1" rows={3} />
                                  </div>

                                  {/* Evidence Files Section */}
                                  <div className="border-t pt-4">
                                    <h4 className="font-medium mb-2">Evidence Files</h4>
                                    {evidenceFiles.length > 0 ? (
                                      <div className="space-y-2 mb-4">
                                        {evidenceFiles.map(file => (
                                          <div key={file.id} className="flex items-center justify-between bg-slate-50 p-2 rounded">
                                            <div className="flex items-center gap-2">
                                              <FileText className="w-4 h-4" />
                                              <span className="text-sm">{file.filename}</span>
                                            </div>
                                            {isAdmin && (
                                              <Button type="button" variant="ghost" size="sm"
                                                onClick={() => handleDeleteEvidence(file.id, control.control_id)}>
                                                <Trash2 className="w-4 h-4 text-red-500" />
                                              </Button>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <p className="text-sm text-gray-500 mb-4">No evidence files uploaded</p>
                                    )}
                                    
                                    {isAdmin && (
                                      <div className="space-y-2">
                                        <Input type="file" onChange={(e) => handleFileUpload(e, control.control_id)}
                                          disabled={uploadingFile} />
                                      </div>
                                    )}
                                  </div>

                                  {/* Recommendation */}
                                  {(control.status === 'No' || control.status === 'Partially') && (
                                    <div className="bg-amber-50 border border-amber-200 rounded p-3">
                                      <h4 className="font-medium text-amber-800 mb-1">Recommendation</h4>
                                      <p className="text-sm text-amber-700">{generateRecommendation(control)}</p>
                                    </div>
                                  )}
                                  
                                  {isAdmin && (
                                    <div className="flex justify-end gap-2 pt-4">
                                      <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                                      <Button type="submit">Save Changes</Button>
                                    </div>
                                  )}
                                </form>
                              )}
                            </DialogContent>
                          </Dialog>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// Export individual framework page components for backwards compatibility
export const ISO27001PageNew = () => <GenericFrameworkPage frameworkKey="ISO27001" />;
export const SOC2PageNew = () => <GenericFrameworkPage frameworkKey="SOC2" />;
export const Essential8PageNew = () => <GenericFrameworkPage frameworkKey="Essential8" />;
export const PCIDSSPageNew = () => <GenericFrameworkPage frameworkKey="PCIDSS" />;
export const HIPAAPageNew = () => <GenericFrameworkPage frameworkKey="HIPAA" />;
export const ISO42001PageNew = () => <GenericFrameworkPage frameworkKey="ISO42001" />;
